import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line
} from 'recharts';
import {
  getEvents,
  clearAllEvents,
  DBRow,
  computeUniqueVisitors,
  computeConversionRate,
  computeUTMBreakdown,
  computeDeviceBreakdown,
  computeDailyTrend,
} from '../../utils/analytics';
import {
  getDateRangeBounds, dateRangeOptions, DateRange, getDateKey, formatDate
} from '../../utils/dateFilters';
import ApiKeysTab from './components/ApiKeysTab';

const PAGE_LABELS: Record<string, { label: string; color: string; icon: string }> = {
  home:              { label: 'Home Page',        color: '#3B82F6', icon: 'ri-home-4-fill' },
  about:             { label: 'My $4,500 Story',  color: '#EAB308', icon: 'ri-file-text-fill' },
  'family-shield':   { label: 'Family Shield',    color: '#10B981', icon: 'ri-shield-user-fill' },
  'landlord-shield': { label: 'Landlord Shield',  color: '#8B5CF6', icon: 'ri-building-fill' },
  'id-shield':       { label: 'ID Shield',        color: '#EC4899', icon: 'ri-fingerprint-fill' },
};

const SOURCE_COLORS: Record<string, string> = {
  Home:              '#3B82F6',
  Story:             '#EAB308',
  'Family Shield':   '#10B981',
  'Landlord Shield': '#8B5CF6',
  'ID Shield':       '#EC4899',
  Unknown:           '#6B7280',
};

const CLICK_COLORS = [
  '#DC2626', '#F97316', '#EAB308', '#22C55E',
  '#06B6D4', '#8B5CF6', '#EC4899', '#14B8A6',
];

const DEVICE_COLORS = {
  desktop: '#3B82F6',
  mobile:  '#10B981',
  tablet:  '#F59E0B',
};

type TabType = 'overview' | 'pages' | 'clicks' | 'utm' | 'devices' | 'apikeys';

export default function DashboardPage() {
  const navigate = useNavigate();
  const [dateRange, setDateRange]           = useState<DateRange>('all-time');
  const [rows, setRows]                     = useState<DBRow[]>([]);
  const [loading, setLoading]               = useState(true);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [activeTab, setActiveTab]           = useState<TabType>('overview');
  const [utmSortKey, setUtmSortKey]         = useState<'campaign' | 'visits' | 'conversions' | 'rate'>('visits');
  const [utmSortDir, setUtmSortDir]         = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    const isAuthenticated = sessionStorage.getItem('bpml_auth') === 'true';
    if (!isAuthenticated) navigate('/dashboard/login');
  }, [navigate]);

  const { start: startTs, end: endTs } = useMemo(() => getDateRangeBounds(dateRange), [dateRange]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const data = await getEvents(startTs, endTs);
    setRows(data);
    setLoading(false);
  }, [startTs, endTs]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Derived stats ────────────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const pageViews = rows.filter(r => r.type === 'page_view');
    const clicks    = rows.filter(r => r.type === 'affiliate_click');
    const uniqueVisitors  = computeUniqueVisitors(rows);
    const conversionRate  = computeConversionRate(rows);

    const byPage: Record<string, number> = {};
    pageViews.forEach(r => {
      const p = r.meta?.page || 'home';
      byPage[p] = (byPage[p] || 0) + 1;
    });

    return { uniqueVisitors, totalViews: pageViews.length, totalClicks: clicks.length, conversionRate, byPage };
  }, [rows]);

  const dailyTrend        = useMemo(() => computeDailyTrend(rows),        [rows]);
  const utmBreakdownRaw   = useMemo(() => computeUTMBreakdown(rows),      [rows]);
  const deviceBreakdown   = useMemo(() => computeDeviceBreakdown(rows),   [rows]);

  // ── Chart data (per-page daily) ──────────────────────────────────────────────
  const chartData = useMemo(() => {
    const grouped: Record<string, Record<string, number> & { date: string }> = {};
    rows.forEach(row => {
      const key = getDateKey(row.timestamp);
      if (!grouped[key]) {
        grouped[key] = { date: key, home: 0, about: 0, 'family-shield': 0, 'landlord-shield': 0, 'id-shield': 0, clicks: 0 };
      }
      if (row.type === 'page_view') {
        const p = row.meta?.page || 'home';
        grouped[key][p] = (grouped[key][p] || 0) + 1;
      } else {
        grouped[key].clicks = (grouped[key].clicks || 0) + 1;
      }
    });
    return Object.values(grouped).sort((a, b) => a.date.localeCompare(b.date));
  }, [rows]);

  // ── Clicks by source page ────────────────────────────────────────────────────
  const clicksBySourcePage = useMemo(() => {
    const map: Record<string, number> = {};
    rows.filter(r => r.type === 'affiliate_click').forEach(r => {
      const src = r.meta?.sourcePage || 'Unknown';
      map[src] = (map[src] || 0) + 1;
    });
    return Object.entries(map).map(([page, count]) => ({ page, count })).sort((a, b) => b.count - a.count);
  }, [rows]);

  // ── Clicks by link type ──────────────────────────────────────────────────────
  const clicksByLinkType = useMemo(() => {
    const map: Record<string, { count: number; byPage: Record<string, number> }> = {};
    rows.filter(r => r.type === 'affiliate_click').forEach(r => {
      const lt  = r.meta?.linkType   || 'Unknown';
      const src = r.meta?.sourcePage || 'Unknown';
      if (!map[lt]) map[lt] = { count: 0, byPage: {} };
      map[lt].count += 1;
      map[lt].byPage[src] = (map[lt].byPage[src] || 0) + 1;
    });
    return Object.entries(map).map(([type, data]) => ({ type, ...data })).sort((a, b) => b.count - a.count);
  }, [rows]);

  // ── UTM sorted ───────────────────────────────────────────────────────────────
  const utmBreakdown = useMemo(() => {
    return [...utmBreakdownRaw].sort((a, b) => {
      const aVal = a[utmSortKey];
      const bVal = b[utmSortKey];
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return utmSortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return utmSortDir === 'asc' ? (aVal as number) - (bVal as number) : (bVal as number) - (aVal as number);
    });
  }, [utmBreakdownRaw, utmSortKey, utmSortDir]);

  const sourcePiData = clicksBySourcePage.map(d => ({ name: d.page, value: d.count }));
  const devicePiData = [
    { name: 'Desktop', value: deviceBreakdown.desktop.count },
    { name: 'Mobile',  value: deviceBreakdown.mobile.count  },
    { name: 'Tablet',  value: deviceBreakdown.tablet.count  },
  ].filter(d => d.value > 0);

  const handleLogout = () => {
    sessionStorage.removeItem('bpml_auth');
    navigate('/dashboard/login');
  };

  const handleClearData = async () => {
    if (showClearConfirm) {
      await clearAllEvents();
      setRows([]);
      setShowClearConfirm(false);
    } else {
      setShowClearConfirm(true);
    }
  };

  const handleUtmSort = (key: typeof utmSortKey) => {
    if (utmSortKey === key) {
      setUtmSortDir(utmSortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setUtmSortKey(key);
      setUtmSortDir('desc');
    }
  };

  return (
    <div className="min-h-screen bg-[#0a1628] text-white">
      {/* ── Header ── */}
      <header className="bg-[#0f1f3a] border-b border-gray-700 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-[#DC2626] rounded-lg flex items-center justify-center">
              <i className="ri-bar-chart-2-fill text-white text-lg"></i>
            </div>
            <h1 className="text-xl font-bold">Analytics Dashboard</h1>
            {loading && (
              <span className="text-xs text-gray-400 flex items-center gap-1">
                <i className="ri-loader-4-line animate-spin"></i> Loading…
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={fetchData}
              className="px-3 py-2 bg-[#1a2f4a] hover:bg-[#243d5c] rounded-lg transition-colors whitespace-nowrap cursor-pointer text-sm"
              title="Refresh data"
            >
              <i className="ri-refresh-line"></i>
            </button>
            <button
              onClick={handleLogout}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg transition-colors whitespace-nowrap cursor-pointer text-sm"
            >
              <i className="ri-logout-box-r-line mr-2"></i>Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* ── Date Range ── */}
        <div className="mb-8">
          <p className="text-sm font-medium text-gray-400 mb-3">Date Range</p>
          <div className="flex flex-wrap gap-2">
            {dateRangeOptions.map(opt => (
              <button
                key={opt.value}
                onClick={() => setDateRange(opt.value)}
                className={`px-4 py-2 rounded-lg transition-colors whitespace-nowrap cursor-pointer text-sm ${
                  dateRange === opt.value
                    ? 'bg-red-600 text-white'
                    : 'bg-[#0f1f3a] text-gray-300 hover:bg-[#1a2f4a]'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* ── Summary Cards ── */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-[#0f1f3a] rounded-xl p-5 border border-gray-700">
            <div className="flex items-center gap-2 mb-1">
              <i className="ri-user-line text-blue-400"></i>
              <p className="text-gray-400 text-xs">Unique Visitors</p>
            </div>
            <p className="text-3xl font-bold">{stats.uniqueVisitors.toLocaleString()}</p>
            <p className="text-gray-500 text-xs mt-1">Distinct users</p>
          </div>
          <div className="bg-[#0f1f3a] rounded-xl p-5 border border-gray-700">
            <div className="flex items-center gap-2 mb-1">
              <i className="ri-file-list-line text-purple-400"></i>
              <p className="text-gray-400 text-xs">Total Pageviews</p>
            </div>
            <p className="text-3xl font-bold">{stats.totalViews.toLocaleString()}</p>
            <p className="text-gray-500 text-xs mt-1">All pages combined</p>
          </div>
          <div className="bg-[#0f1f3a] rounded-xl p-5 border border-gray-700">
            <div className="flex items-center gap-2 mb-1">
              <i className="ri-external-link-line text-green-400"></i>
              <p className="text-gray-400 text-xs">Affiliate Clicks</p>
            </div>
            <p className="text-3xl font-bold">{stats.totalClicks.toLocaleString()}</p>
            <p className="text-gray-500 text-xs mt-1">Outbound conversions</p>
          </div>
          <div className="bg-[#0f1f3a] rounded-xl p-5 border border-gray-700">
            <div className="flex items-center gap-2 mb-1">
              <i className="ri-percent-line text-yellow-400"></i>
              <p className="text-gray-400 text-xs">Conversion Rate</p>
            </div>
            <p className="text-3xl font-bold">{stats.conversionRate.toFixed(1)}%</p>
            <p className="text-gray-500 text-xs mt-1">Clicks ÷ Visitors</p>
          </div>
        </div>

        {/* ── Tabs ── */}
        <div className="flex gap-1 bg-[#0f1f3a] rounded-xl p-1 mb-8 overflow-x-auto">
          {(['overview', 'pages', 'clicks', 'utm', 'devices', 'apikeys'] as const).map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-5 py-2 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer capitalize ${
                activeTab === tab ? 'bg-[#DC2626] text-white' : 'text-gray-400 hover:text-white'
              }`}
            >
              {tab === 'overview' ? 'Overview' :
               tab === 'pages'    ? 'Page Views' :
               tab === 'clicks'   ? 'Outbound Clicks' :
               tab === 'utm'      ? 'UTM Campaigns' :
               tab === 'devices'  ? 'Devices' : 'API Keys'}
            </button>
          ))}
        </div>

        {/* ══ OVERVIEW ══ */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            <div className="bg-[#0f1f3a] rounded-xl p-6 border border-gray-700">
              <h2 className="text-lg font-bold mb-6">Daily Trend: Visitors &amp; Conversions</h2>
              {dailyTrend.length > 0 ? (
                <ResponsiveContainer width="100%" height={320}>
                  <LineChart data={dailyTrend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis dataKey="date" stroke="#9CA3AF" tick={{ fontSize: 11 }}
                      tickFormatter={v => { const d = new Date(v); return `${d.getMonth()+1}/${d.getDate()}`; }} />
                    <YAxis stroke="#9CA3AF" tick={{ fontSize: 11 }} />
                    <Tooltip contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px', color: '#fff' }} />
                    <Legend />
                    <Line type="monotone" dataKey="visitors"    stroke="#3B82F6" strokeWidth={2} name="Unique Visitors" dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="conversions" stroke="#10B981" strokeWidth={2} name="Conversions"     dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center py-16 text-gray-500">{loading ? 'Loading data…' : 'No data for selected range'}</div>
              )}
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-[#0f1f3a] rounded-xl p-6 border border-gray-700">
                <h2 className="text-lg font-bold mb-4">Views by Page</h2>
                <div className="space-y-3">
                  {Object.entries(PAGE_LABELS).map(([key, meta]) => {
                    const count = stats.byPage[key] || 0;
                    const pct   = stats.totalViews > 0 ? Math.round((count / stats.totalViews) * 100) : 0;
                    return (
                      <div key={key}>
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <i className={`${meta.icon} text-sm`} style={{ color: meta.color }}></i>
                            <span className="text-sm text-gray-300">{meta.label}</span>
                          </div>
                          <span className="text-sm font-bold">{count.toLocaleString()} <span className="text-gray-500 font-normal">({pct}%)</span></span>
                        </div>
                        <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                          <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: meta.color }}></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="bg-[#0f1f3a] rounded-xl p-6 border border-gray-700">
                <h2 className="text-lg font-bold mb-4">Clicks by Source Page</h2>
                {clicksBySourcePage.length > 0 ? (
                  <div className="space-y-3">
                    {clicksBySourcePage.map(({ page, count }) => {
                      const pct   = stats.totalClicks > 0 ? Math.round((count / stats.totalClicks) * 100) : 0;
                      const color = SOURCE_COLORS[page] || SOURCE_COLORS.Unknown;
                      return (
                        <div key={page}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm text-gray-300">{page} Page</span>
                            <span className="text-sm font-bold">{count.toLocaleString()} <span className="text-gray-500 font-normal">({pct}%)</span></span>
                          </div>
                          <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                            <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: color }}></div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500 text-sm">{loading ? 'Loading…' : 'No clicks recorded yet'}</div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ══ PAGE VIEWS ══ */}
        {activeTab === 'pages' && (
          <div className="space-y-6">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(PAGE_LABELS).map(([key, meta]) => {
                const count = stats.byPage[key] || 0;
                const pct   = stats.totalViews > 0 ? Math.round((count / stats.totalViews) * 100) : 0;
                const urlPath = key === 'home' ? '/' : `/${key}`;
                return (
                  <div key={key} className="bg-[#0f1f3a] rounded-xl p-6 border border-gray-700">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${meta.color}20` }}>
                        <i className={`${meta.icon} text-xl`} style={{ color: meta.color }}></i>
                      </div>
                      <div>
                        <p className="font-bold text-white">{meta.label}</p>
                        <p className="text-xs text-gray-500">{urlPath}</p>
                      </div>
                    </div>
                    <p className="text-4xl font-black mb-1">{count.toLocaleString()}</p>
                    <p className="text-gray-400 text-sm">{pct}% of all page views</p>
                  </div>
                );
              })}
            </div>

            <div className="bg-[#0f1f3a] rounded-xl p-6 border border-gray-700">
              <h2 className="text-lg font-bold mb-6">Daily Page Views by Page</h2>
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis dataKey="date" stroke="#9CA3AF" tick={{ fontSize: 11 }}
                      tickFormatter={v => { const d = new Date(v); return `${d.getMonth()+1}/${d.getDate()}`; }} />
                    <YAxis stroke="#9CA3AF" tick={{ fontSize: 11 }} />
                    <Tooltip contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px', color: '#fff' }}
                      labelFormatter={v => formatDate(new Date(v).getTime())} />
                    <Legend />
                    {Object.entries(PAGE_LABELS).map(([key, meta]) => (
                      <Bar key={key} dataKey={key} fill={meta.color} name={meta.label} radius={[3,3,0,0]} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center py-16 text-gray-500">{loading ? 'Loading data…' : 'No data for selected range'}</div>
              )}
            </div>
          </div>
        )}

        {/* ══ OUTBOUND CLICKS ══ */}
        {activeTab === 'clicks' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-bold mb-4">Clicks by Source Page</h2>
              <div className="grid md:grid-cols-3 gap-4">
                {clicksBySourcePage.length > 0 ? clicksBySourcePage.map(({ page, count }) => {
                  const pct      = stats.totalClicks > 0 ? Math.round((count / stats.totalClicks) * 100) : 0;
                  const convRate = stats.uniqueVisitors > 0 ? ((count / stats.uniqueVisitors) * 100).toFixed(1) : '0.0';
                  const color    = SOURCE_COLORS[page] || SOURCE_COLORS.Unknown;
                  return (
                    <div key={page} className="bg-[#0f1f3a] rounded-xl p-5 border border-gray-700">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }}></div>
                        <p className="text-gray-300 text-sm font-medium">{page} Page</p>
                      </div>
                      <p className="text-4xl font-black mb-1">{count.toLocaleString()}</p>
                      <p className="text-gray-500 text-xs">{pct}% of clicks • {convRate}% conv. rate</p>
                    </div>
                  );
                }) : (
                  <div className="col-span-3 text-center py-12 text-gray-500">{loading ? 'Loading…' : 'No clicks recorded yet'}</div>
                )}
              </div>
            </div>

            {clicksBySourcePage.length > 0 && (
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-[#0f1f3a] rounded-xl p-6 border border-gray-700 flex flex-col items-center justify-center">
                  <h2 className="text-lg font-bold mb-4 self-start">Click Share by Page</h2>
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie data={sourcePiData} cx="50%" cy="50%" outerRadius={80} dataKey="value"
                        label={({ name, percent }) => `${name} ${Math.round(percent * 100)}%`}>
                        {sourcePiData.map((entry, i) => (
                          <Cell key={entry.name} fill={SOURCE_COLORS[entry.name] || CLICK_COLORS[i % CLICK_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px', color: '#fff' }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                <div className="bg-[#0f1f3a] rounded-xl p-6 border border-gray-700">
                  <h2 className="text-lg font-bold mb-4">Clicks by Button / Link</h2>
                  <div className="space-y-3 overflow-y-auto max-h-64 pr-1">
                    {clicksByLinkType.map(({ type, count, byPage }, idx) => (
                      <div key={type} className="border border-gray-700 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: CLICK_COLORS[idx % CLICK_COLORS.length] }}></div>
                            <span className="text-sm font-semibold text-white">{type}</span>
                          </div>
                          <span className="text-sm font-bold text-white">{count}</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {Object.entries(byPage).map(([pg, cnt]) => (
                            <span key={pg} className="text-xs px-2 py-0.5 rounded-full"
                              style={{ backgroundColor: `${SOURCE_COLORS[pg] || '#6B7280'}25`, color: SOURCE_COLORS[pg] || '#9CA3AF' }}>
                              {pg}: {cnt}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div className="bg-[#0f1f3a] rounded-xl p-6 border border-gray-700">
              <h2 className="text-lg font-bold mb-4">Full Click Breakdown</h2>
              {clicksByLinkType.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-700">
                        <th className="text-left py-3 px-4 text-gray-400 font-medium">Button / Link</th>
                        {clicksBySourcePage.map(({ page }) => (
                          <th key={page} className="text-center py-3 px-4 text-gray-400 font-medium">From {page}</th>
                        ))}
                        <th className="text-right py-3 px-4 text-gray-400 font-medium">Total</th>
                        <th className="text-right py-3 px-4 text-gray-400 font-medium">Conv. Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {clicksByLinkType.map(({ type, count, byPage }) => {
                        const convRate = stats.uniqueVisitors > 0 ? ((count / stats.uniqueVisitors) * 100).toFixed(1) : '0.0';
                        return (
                          <tr key={type} className="border-b border-gray-800 hover:bg-[#1a2f4a] transition-colors">
                            <td className="py-3 px-4 font-medium">{type}</td>
                            {clicksBySourcePage.map(({ page }) => (
                              <td key={page} className="py-3 px-4 text-center" style={{ color: SOURCE_COLORS[page] || '#9CA3AF' }}>
                                {byPage[page] || 0}
                              </td>
                            ))}
                            <td className="py-3 px-4 text-right font-bold">{count}</td>
                            <td className="py-3 px-4 text-right text-yellow-400">{convRate}%</td>
                          </tr>
                        );
                      })}
                      <tr className="bg-[#1a2f4a]">
                        <td className="py-3 px-4 font-bold text-white">TOTAL</td>
                        {clicksBySourcePage.map(({ page, count }) => (
                          <td key={page} className="py-3 px-4 text-center font-bold" style={{ color: SOURCE_COLORS[page] || '#9CA3AF' }}>{count}</td>
                        ))}
                        <td className="py-3 px-4 text-right font-bold text-white">{stats.totalClicks}</td>
                        <td className="py-3 px-4 text-right font-bold text-yellow-400">{stats.conversionRate.toFixed(1)}%</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">{loading ? 'Loading…' : 'No clicks recorded yet'}</div>
              )}
            </div>
          </div>
        )}

        {/* ══ UTM CAMPAIGNS ══ */}
        {activeTab === 'utm' && (
          <div className="space-y-6">
            <div className="bg-[#0f1f3a] rounded-xl p-6 border border-gray-700">
              <h2 className="text-lg font-bold mb-4">UTM Campaign Performance</h2>
              {utmBreakdown.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-700">
                        <th className="text-left py-3 px-4 text-gray-400 font-medium cursor-pointer hover:text-white transition-colors" onClick={() => handleUtmSort('campaign')}>
                          Campaign {utmSortKey === 'campaign' && (utmSortDir === 'asc' ? '↑' : '↓')}
                        </th>
                        <th className="text-left py-3 px-4 text-gray-400 font-medium">Source</th>
                        <th className="text-left py-3 px-4 text-gray-400 font-medium">Medium</th>
                        <th className="text-center py-3 px-4 text-gray-400 font-medium cursor-pointer hover:text-white transition-colors" onClick={() => handleUtmSort('visits')}>
                          Visits {utmSortKey === 'visits' && (utmSortDir === 'asc' ? '↑' : '↓')}
                        </th>
                        <th className="text-center py-3 px-4 text-gray-400 font-medium cursor-pointer hover:text-white transition-colors" onClick={() => handleUtmSort('conversions')}>
                          Conversions {utmSortKey === 'conversions' && (utmSortDir === 'asc' ? '↑' : '↓')}
                        </th>
                        <th className="text-right py-3 px-4 text-gray-400 font-medium cursor-pointer hover:text-white transition-colors" onClick={() => handleUtmSort('rate')}>
                          Conv. Rate {utmSortKey === 'rate' && (utmSortDir === 'asc' ? '↑' : '↓')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {utmBreakdown.map((row, idx) => (
                        <tr key={idx} className="border-b border-gray-800 hover:bg-[#1a2f4a] transition-colors">
                          <td className="py-3 px-4 font-medium">{row.campaign}</td>
                          <td className="py-3 px-4 text-gray-400">{row.source}</td>
                          <td className="py-3 px-4 text-gray-400">{row.medium}</td>
                          <td className="py-3 px-4 text-center text-blue-400">{row.visits.toLocaleString()}</td>
                          <td className="py-3 px-4 text-center text-green-400">{row.conversions.toLocaleString()}</td>
                          <td className="py-3 px-4 text-right font-bold text-yellow-400">{row.rate.toFixed(1)}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">{loading ? 'Loading…' : 'No UTM data recorded yet'}</div>
              )}
            </div>

            {utmBreakdown.length > 0 && (
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-[#0f1f3a] rounded-xl p-5 border border-gray-700">
                  <p className="text-gray-400 text-xs mb-1">Total Campaigns</p>
                  <p className="text-3xl font-bold">{utmBreakdown.length}</p>
                  <p className="text-gray-500 text-xs mt-1">Unique UTM campaigns</p>
                </div>
                <div className="bg-[#0f1f3a] rounded-xl p-5 border border-gray-700">
                  <p className="text-gray-400 text-xs mb-1">Top Campaign</p>
                  <p className="text-xl font-bold truncate">{utmBreakdown[0]?.campaign || 'N/A'}</p>
                  <p className="text-gray-500 text-xs mt-1">{utmBreakdown[0]?.visits || 0} visits</p>
                </div>
                <div className="bg-[#0f1f3a] rounded-xl p-5 border border-gray-700">
                  <p className="text-gray-400 text-xs mb-1">Best Conv. Rate</p>
                  <p className="text-3xl font-bold text-yellow-400">
                    {Math.max(...utmBreakdown.map(u => u.rate)).toFixed(1)}%
                  </p>
                  <p className="text-gray-500 text-xs mt-1">Highest performing</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ══ DEVICES ══ */}
        {activeTab === 'devices' && (
          <div className="space-y-6">
            <div className="grid md:grid-cols-3 gap-4">
              {(['desktop', 'mobile', 'tablet'] as const).map(d => {
                const icons = { desktop: 'ri-computer-line', mobile: 'ri-smartphone-line', tablet: 'ri-tablet-line' };
                const labels = { desktop: 'Computer users', mobile: 'Phone users', tablet: 'Tablet users' };
                const data = deviceBreakdown[d];
                return (
                  <div key={d} className="bg-[#0f1f3a] rounded-xl p-6 border border-gray-700">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${DEVICE_COLORS[d]}20` }}>
                        <i className={`${icons[d]} text-2xl`} style={{ color: DEVICE_COLORS[d] }}></i>
                      </div>
                      <div>
                        <p className="font-bold text-white capitalize">{d}</p>
                        <p className="text-xs text-gray-500">{labels[d]}</p>
                      </div>
                    </div>
                    <p className="text-5xl font-black mb-1">{data.count.toLocaleString()}</p>
                    <p className="text-gray-400 text-sm">{data.percentage.toFixed(1)}% of all pageviews</p>
                  </div>
                );
              })}
            </div>

            {devicePiData.length > 0 && (
              <div className="bg-[#0f1f3a] rounded-xl p-6 border border-gray-700">
                <h2 className="text-lg font-bold mb-6">Device Distribution</h2>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={devicePiData} cx="50%" cy="50%" outerRadius={100} dataKey="value"
                      label={({ name, percent }) => `${name} ${Math.round(percent * 100)}%`}>
                      {devicePiData.map(entry => (
                        <Cell key={entry.name} fill={DEVICE_COLORS[entry.name.toLowerCase() as keyof typeof DEVICE_COLORS]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px', color: '#fff' }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        )}

        {/* ══ API KEYS ══ */}
        {activeTab === 'apikeys' && <ApiKeysTab />}

        {/* ── Clear Data ── */}
        <div className="flex justify-center mt-10">
          <button
            onClick={handleClearData}
            onBlur={() => setShowClearConfirm(false)}
            className={`px-6 py-3 rounded-lg transition-colors whitespace-nowrap cursor-pointer text-sm ${
              showClearConfirm ? 'bg-red-600 hover:bg-red-700' : 'bg-gray-700 hover:bg-gray-600'
            }`}
          >
            {showClearConfirm ? '⚠️ Click Again to Confirm Clear' : 'Clear All Data'}
          </button>
        </div>
      </div>
    </div>
  );
}
